import { useTranslation } from "react-i18next";
import type { ClassificationState, SessionPerson } from "../api/session";
import { useSession } from "../session/SessionContext";

const descriptionKeyByState: Record<Exclude<ClassificationState, "Active">, string> = {
  Unknown: "notEligible.descriptionUnknown",
  Pending: "notEligible.descriptionPending",
  Inactive: "notEligible.descriptionInactive",
};

/**
 * The `UR-V-01` response: an authenticated identity the portal does not consider eligible, shown
 * as its own explicit message rather than an empty dashboard or a permission-denied error — those
 * are different situations and, per the plan, must not be conflated.
 *
 * @param props.person - The signed-in identity, as reported by `GET /api/session/me`.
 * @param props.classificationState - Which of the three non-eligible states applies.
 */
export function NotEligibleNotice({
  person,
  classificationState,
}: {
  person: SessionPerson;
  classificationState: Exclude<ClassificationState, "Active">;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useSession();

  return (
    <div role="alert">
      <h1>{t("notEligible.title")}</h1>
      <p>{t("notEligible.signedInAs", { email: person.email })}</p>
      <p>{t(descriptionKeyByState[classificationState])}</p>
      <button type="button" className="button button-secondary" onClick={() => void signOut()}>
        {t("notEligible.signOut")}
      </button>
    </div>
  );
}
